import { Chess } from 'chess.js';
import type { Key } from 'chessground/types';
import type { Puzzle } from './api';

/**
 * State machine immutable du Tactics Drill (T2 MVP2.5).
 *
 * Pourquoi `useReducer` plutôt que `useState` :
 *  - Évite de mélanger UI rendering et logique de transition (le bug A2 venait
 *    en partie d'un `setState` qui retenait une instance `Chess` mutable
 *    partagée entre handler tactile et `setTimeout` adversaire).
 *  - Reducer = fonction pure : la Chess instance est reconstruite localement à
 *    chaque action depuis `state.fen` (source unique de vérité). Pas d'effet
 *    de bord dans le reducer (pas de fetch, pas de setTimeout, pas de
 *    `console.log`).
 *  - Le coup adversaire passe par un `useEffect(awaiting_engine)` qui dispatch
 *    `MOVE_ENGINE` via `requestAnimationFrame` (cf. WAKEUP_MVP2.5 §2 T2). Sur
 *    iOS Safari le `setTimeout` inline est throttlé pendant qu'un touch event
 *    est consommé, ce qui bloque le déclenchement du coup adversaire et donne
 *    l'impression que le board "se bloque" après le premier coup.
 *
 * Le state UI utilise une discriminated union sur `kind` :
 *  - `idle` : avant le premier puzzle de la session.
 *  - `awaiting_player` : board interactif, Léon doit jouer `puzzle.solution[cursor]`.
 *  - `awaiting_engine` : Léon vient de jouer le bon coup, `nextEngineMove` est queued.
 *  - `success` : puzzle résolu (Léon a joué le dernier coup de la séquence).
 *  - `fail` : Léon a joué un coup différent du coup attendu.
 */

export type TacticsDrillState =
  | { kind: 'idle' }
  | {
      kind: 'awaiting_player';
      puzzle: Puzzle;
      fen: string;
      cursor: number;
      lastMove: [Key, Key] | undefined;
    }
  | {
      kind: 'awaiting_engine';
      puzzle: Puzzle;
      fen: string;
      cursor: number;
      lastMove: [Key, Key];
      /** Coup adverse à rejouer (extrait de puzzle.solution[cursor]). */
      nextEngineMove: string;
    }
  | {
      kind: 'success';
      puzzle: Puzzle;
      fen: string;
      cursor: number;
      lastMove: [Key, Key] | undefined;
      /** SAN du coup gagnant joué par Léon. Utilisé pour logAttempt. */
      playedMove: string;
      /** SAN du coup attendu = playedMove en cas de succès. */
      expectedMove: string;
      /** Durée totale du puzzle (utile pour `classifySuccessOutcome`). */
      timeSpentMs: number;
    }
  | {
      kind: 'fail';
      puzzle: Puzzle;
      /**
       * FEN avant la tentative ratée (état au moment de jouer). Permet de
       * conserver le board figé sur la position du puzzle pour que Léon voie
       * sa propre erreur (pas de auto-revert mid-flight).
       */
      fen: string;
      cursor: number;
      lastMove: [Key, Key] | undefined;
      expectedMove: string;
      playedMove: string;
      timeSpentMs: number;
      outcomeLogged: boolean;
    };

export type TacticsDrillAction =
  | { type: 'START_PUZZLE'; puzzle: Puzzle }
  | {
      type: 'MOVE_PLAYER';
      from: Key;
      to: Key;
      /**
       * Pièce de promotion UCI ('q'/'r'/'b'/'n') si le coup atteint la 1ère
       * ou 8ème rangée avec un pion. Détecté par `Board.detectAutoPromotion`
       * et propagé via `BoardMoveEvent.promotion`. F-4 P1 chessboards :
       * chess.js v1 throw sur `chess.move({from, to})` si la promotion
       * est requise mais omise → coup ignoré silencieusement → puzzle
       * avec promotion bloqué.
       */
      promotion?: 'q' | 'r' | 'b' | 'n';
      /** Performance.now() au moment du dispatch (pour calculer le délai). */
      now: number;
      /** performance.now() au démarrage du puzzle (référence). */
      startedAt: number;
    }
  | { type: 'MOVE_ENGINE' }
  | { type: 'MARK_FAIL_LOGGED' };

/**
 * Reducer pur. Toute Chess instance est créée localement et jetée après
 * usage : le state ne porte que la FEN (string). C'est ce qui permet aux
 * tests de comparer deux états successifs sans piéger d'instance mutable.
 */
export function tacticsDrillReducer(
  state: TacticsDrillState,
  action: TacticsDrillAction,
): TacticsDrillState {
  switch (action.type) {
    case 'START_PUZZLE': {
      return {
        kind: 'awaiting_player',
        puzzle: action.puzzle,
        fen: action.puzzle.fen,
        cursor: 0,
        lastMove: undefined,
      };
    }

    case 'MOVE_PLAYER': {
      if (state.kind !== 'awaiting_player') return state;
      const { puzzle, fen, cursor } = state;
      const expectedSan = puzzle.solution[cursor];
      if (!expectedSan) return state;

      const chess = new Chess(fen);
      let playedSan: string | null = null;
      try {
        // F-4 : `promotion` injecté conditionnellement pour respecter
        // `exactOptionalPropertyTypes` (chess.js refuse `promotion: undefined`).
        const res = chess.move({
          from: action.from,
          to: action.to,
          ...(action.promotion ? { promotion: action.promotion } : {}),
        });
        playedSan = res?.san ?? null;
      } catch {
        playedSan = null;
      }
      if (playedSan === null) return state;

      const timeSpentMs = Math.round(action.now - action.startedAt);

      if (playedSan !== expectedSan) {
        return {
          kind: 'fail',
          puzzle,
          // Garde la FEN d'AVANT la tentative ratée — Léon voit la position
          // du puzzle, pas le résultat de son coup erroné.
          fen,
          cursor,
          lastMove: undefined,
          expectedMove: expectedSan,
          playedMove: playedSan,
          timeSpentMs,
          outcomeLogged: false,
        };
      }

      const nextCursor = cursor + 1;
      const opponentReply = puzzle.solution[nextCursor];

      if (!opponentReply) {
        return {
          kind: 'success',
          puzzle,
          fen: chess.fen(),
          cursor: nextCursor,
          lastMove: [action.from, action.to],
          playedMove: playedSan,
          expectedMove: expectedSan,
          timeSpentMs,
        };
      }

      return {
        kind: 'awaiting_engine',
        puzzle,
        fen: chess.fen(),
        cursor: nextCursor,
        lastMove: [action.from, action.to],
        nextEngineMove: opponentReply,
      };
    }

    case 'MOVE_ENGINE': {
      if (state.kind !== 'awaiting_engine') return state;
      const { puzzle, fen, cursor, nextEngineMove } = state;

      const chess = new Chess(fen);
      try {
        const res = chess.move(nextEngineMove);
        if (!res) return state;
      } catch {
        // Coup adversaire pré-calculé invalide : on garde le state actuel.
        // Cas pathologique : la séquence du puzzle est corrompue. Le test
        // de validation `validate-tactics-sample.ts --full` couvre ça en CI.
        return state;
      }

      return {
        kind: 'awaiting_player',
        puzzle,
        fen: chess.fen(),
        cursor: cursor + 1,
        lastMove: undefined,
      };
    }

    case 'MARK_FAIL_LOGGED': {
      if (state.kind !== 'fail') return state;
      return { ...state, outcomeLogged: true };
    }

    default:
      return state;
  }
}

export const initialTacticsDrillState: TacticsDrillState = { kind: 'idle' };
